import { API_URL } from '../api/client';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Attachments live in Cloudinary and already carry a full https link, so this
 * usually just hands it back. The relative branch is only a safety net.
 */
export function fileUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

/** 06 Aug 2026 */
export function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** 06 Aug 2026, 04:15 pm */
export function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return `${formatDate(d)}, ${formatTime(d)}`;
}

/** 04:15 pm */
export function formatTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  const h = d.getHours();
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour12).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} ${suffix}`;
}

/** YYYY-MM-DD in local time, for <input type="date"> values. */
export function toInputDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "42 y" / "6 m" */
export function formatAge(patient) {
  if (patient?.age === undefined || patient?.age === null || patient.age === '') return '-';
  const unit = { years: 'y', months: 'm', days: 'd' }[patient.ageUnit || 'years'];
  return `${patient.age} ${unit}`;
}

/** "Male" */
export function titleCase(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatMoney(amount) {
  return `₹${Number(amount || 0).toLocaleString('en-IN')}`;
}

/** "3 days ago", used on the patient timeline. */
export function timeAgo(value) {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

/** Total tablets/doses for a medicine line, so the chemist knows the count. */
/**
 * Duration is one free-text box - "5 days", "2 weeks", "10 દિવસ". Pull the
 * count and the unit back out of it. Anything unrecognised counts as days.
 */
const DURATION_UNITS = [
  { unit: 'months', pattern: /month|mth|મહિન/i },
  { unit: 'weeks', pattern: /week|wk|અઠવાડ/i },
  { unit: 'days', pattern: /day|દિવસ/i },
];

export function parseDuration(text) {
  const raw = String(text ?? '');
  const match = DURATION_UNITS.find((u) => u.pattern.test(raw));
  return { count: parseFloat(raw) || 0, unit: match ? match.unit : 'days' };
}

/** "2 weeks" -> 14. Part days round up, so 2.5 days is a 3 day course. */
export function durationInDays(text) {
  const { count, unit } = parseDuration(text);
  return Math.ceil(count * { days: 1, weeks: 7, months: 30 }[unit]);
}

export function calcQuantity(medicine) {
  const perDay = String(medicine.frequency || '')
    .split('-')
    .reduce((sum, part) => sum + (parseFloat(part) || 0), 0);

  const { count, unit } = parseDuration(medicine.duration);
  const qty = perDay * count * { days: 1, weeks: 7, months: 30 }[unit];
  return Math.ceil(qty) || 0;
}
